import { Component, NgZone, OnInit, OnDestroy } from '@angular/core';
import { Subject, Subscription, interval, Observable } from 'rxjs';
import { takeUntil, startWith, take } from 'rxjs/operators';
import { LocalStorageService } from '../local-storage.service';
import { ZendeskService } from '../zendesk.service';
import { AuthService, User } from '../services/ajs-auth.service';
import { ZendeskModeService } from '../services/zendesk-mode.service';
import { MessengerChatAdapter } from '../services/chat/messenger-chat.adapter';
import { loadZendeskSnippet, removeZendeskSnippet } from '../zendesk-snippet';
import { generateUuid } from '../zendesk-uuid';
import { CATEGORIA_COM_CHAT, getCategoriaCliente } from '../categoria-cliente';
import { zendeskLog, ZendeskLogLevel } from '../zendesk-logger';

/**
 * Assinatura mínima do comando global do Zendesk Web Widget (`zE`).
 * Tipamos como função para evitar `any` solto pelo componente.
 */
type ZendeskCommand = (...args: any[]) => void;

declare global {
  interface Window {
    zE?: ZendeskCommand;
  }
}

/**
 * Componente para integração com Zendesk Messenger
 *
 * Só é instanciado quando o `ZendeskModeService` resolve o modo `messenger` —
 * quem decide é o ZendeskWidgetComponent.
 *
 * O gate de público (categoria 1) fica aqui e não no
 * `ZendeskModeService` de propósito: o modo é resolvido uma vez por página, e
 * a categoria precisa acompanhar login e troca de conta sem recarregar. Para
 * as outras categorias o custo é só este polling local, sem chamada de rede.
 *
 * Funcionalidades:
 * - Só inicializa para clientes de categoria 1 (Head Account)
 * - Autenticação via JWT (obtido do backend, cacheado em memória por sessão)
 * - visitorId estável por dispositivo (persistido em localStorage)
 * - Conversas persistentes pelo próprio SDK do Messaging (Conversation ID)
 * - Tags para contexto (custom fields pendentes de configuração no Admin)
 *
 * @see docs/SOLUCAO_COMPLETA_MESSENGER.md
 */
@Component({
  selector: 'mandae-zendesk-messenger',
  templateUrl: './zendesk-messenger.component.html',
  styleUrls: ['./zendesk-messenger.component.scss']
})
export class ZendeskMessengerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private scriptLoaded = false;
  private widgetInitialized = false;
  private initInProgress = false; // Evita inicializações concorrentes disparadas pelo polling
  private currentUserId: string | null = null; // Rastreia o usuário atualmente autenticado
  private categoryAllowed = false; // A categoria do usuário atual tem atendimento (ver hasChatCategory)

  // Circuit breaker de inicialização: falhas persistentes (ad-blocker, CDN fora,
  // key inválida) não devem gerar um loop perpétuo de retentativas a cada 5s.
  // Aplicamos backoff progressivo e desistimos após um número máximo de tentativas.
  private initFailures = 0;
  private initAborted = false;
  private nextInitAttemptAt = 0; // epoch ms; o polling só re-tenta a partir deste instante
  private readonly maxInitFailures = 5;
  private readonly retryDelaysMs = [5000, 30000, 120000, 600000]; // 5s → 30s → 2min → 10min (o último repete)

  // Teto para a confirmação do `loginUser`. O SDK do Zendesk pode nunca invocar
  // o callback de resultado (script bloqueado, handshake que não volta), e sem
  // este teto a Promise de `authenticateAndInit` ficaria pendente para sempre —
  // `initMessenger` nunca liberaria `initInProgress` e o widget morreria sem
  // nova tentativa. Generoso de propósito: cobre busca do JWT + handshake.
  private readonly authTimeoutMs = 20000;

  constructor(
    private localStorageService: LocalStorageService,
    private zendeskService: ZendeskService,
    private authService: AuthService,
    private modeService: ZendeskModeService,
    private messengerChatAdapter: MessengerChatAdapter,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    // Observa mudanças no usuário logado por polling (a fonte de verdade é o
    // AuthService legado do AngularJS, sem um Observable de sessão exposto).
    //
    // Rodamos FORA do Angular (runOutsideAngular): este componente é invisível
    // e o widget é controlado imperativamente via `zE`, então não há binding de
    // template para atualizar. Sem isso, o `interval` dispararia o change
    // detection global a cada 5s por toda a vida da aplicação — custo puro numa SPA.
    this.ngZone.runOutsideAngular(() => {
      interval(5000)
        .pipe(startWith(0), takeUntil(this.destroy$))
        .subscribe(() => this.syncUserState());
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.hideMessenger();
    this.removeZendeskScript();
  }

  /**
   * Reconcilia o estado do widget com o usuário atualmente logado.
   * Inicializa quando um usuário aparece (ou troca) e esconde no logout.
   */
  private syncUserState(): void {
    // Aba em background: não há urgência em reconciliar o estado do widget.
    // Evita consumir CPU/bateria com polling enquanto a página está oculta;
    // ao voltar a ficar visível, o próximo tick retoma normalmente.
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }

    const user = this.authService.getCurrentUser();
    const userId = user && user.id ? user.id.toString() : null;

    // Sem usuário logado: esconde o widget (se aberto) e zera o circuito de retry.
    if (!userId) {
      if (this.widgetInitialized) {
        this.log('Usuário deslogado - escondendo Messenger');
        this.hideMessenger();
      }
      this.currentUserId = null;
      this.resetInitBackoff();
      return;
    }

    // Usuário novo (login ou troca de conta): recomeça do zero — merece uma
    // nova chance mesmo que o usuário anterior tenha esgotado as tentativas.
    if (userId !== this.currentUserId) {
      this.log(`Usuário detectado: ${userId} (anterior: ${this.currentUserId})`);

      // Troca de conta sem logout: derruba o widget do usuário anterior antes
      // de tudo, qualquer que seja a categoria da nova conta. Sem isso, numa
      // troca entre duas contas de categoria 1 o loginUser da nova cairia no
      // ramo de renovação (`widgetInitialized` ainda true) e só sairia por timeout.
      if (this.widgetInitialized) {
        this.hideMessenger();
      }

      this.currentUserId = userId;
      this.resetInitBackoff();
      this.categoryAllowed = this.hasChatCategory(userId);

      if (!this.categoryAllowed) {
        this.log('Categoria do cliente não inclui chat.', 'warn');
        return;
      }

      this.initMessenger(user as User);
      return;
    }

    // Mesmo usuário ainda sem chat: o `ls.user` pode ser gravado (ou trocar de
    // conta) depois de o AuthService já expor o usuário. Reavalia em silêncio
    // até a categoria liberar — sem isso o cliente ficaria sem widget até recarregar.
    if (!this.categoryAllowed) {
      this.categoryAllowed = this.hasChatCategory(userId, false);

      if (!this.categoryAllowed) {
        return;
      }

      this.log(`Categoria do usuário ${userId} liberada após atualização do ls.user`);
      this.initMessenger(user as User);
      return;
    }

    // Mesmo usuário e widget ainda não pronto: re-tenta respeitando o backoff
    // e o limite de tentativas.
    if (!this.widgetInitialized && !this.initAborted && Date.now() >= this.nextInitAttemptAt) {
      this.initMessenger(user as User);
    }
  }

  /**
   * Gate de público: só clientes de categoria 1 (Head Account) têm
   * atendimento. Lido de `ls.user` — o usuário do AuthService não traz a
   * categoria.
   *
   * Só vale o `ls.user` do próprio `userId`: um `ls.user` ausente, ilegível ou
   * ainda da conta anterior conta como "sem chat", e o polling reavalia no
   * próximo tick (ver syncUserState). `verbose` desliga os logs nessas
   * reavaliações, que rodam a cada 5s.
   */
  private hasChatCategory(userId: string, verbose = true): boolean {
    const userData = this.localStorageService.get('ls.user');

    if (!userData) {
      return false;
    }

    try {
      const lsUser = JSON.parse(userData);

      if (!lsUser || String(lsUser.id) !== userId) {
        if (verbose) {
          this.log(`ls.user ainda não é do usuário ${userId} — categoria será reavaliada`, 'warn');
        }
        return false;
      }

      const categoriaCliente = getCategoriaCliente(lsUser);
      if (verbose) {
        this.log(`categoriaCliente: ${categoriaCliente}`);
      }

      return categoriaCliente === CATEGORIA_COM_CHAT;
    } catch (error) {
      if (verbose) {
        this.log(`Não foi possível ler a categoria do cliente: ${error}`, 'error');
      }

      return false;
    }
  }

  /** O usuário ainda é o logado agora, e com categoria que tem chat. */
  private isStillEligible(user: User): boolean {
    return !!user && String(user.id) === this.currentUserId && this.categoryAllowed;
  }

  /**
   * Inicializa o Zendesk Messenger
   */
  private async initMessenger(user: User): Promise<void> {
    // O polling dispara a cada 5s, mas a inicialização é assíncrona e pode
    // demorar mais que isso (carregar script + aguardar o SDK). Sem esta guarda,
    // várias inicializações rodariam em paralelo e o backend seria chamado em
    // duplicidade. `initInProgress` serializa; o `finally` libera para o próximo tick.
    if (this.initInProgress) {
      return;
    }
    this.initInProgress = true;

    try {
      this.log('Iniciando Messenger...');

      // 1. Carregar script do Zendesk Messenger (se ainda não carregou)
      if (!this.scriptLoaded) {
        await this.loadZendeskScript();
        this.scriptLoaded = true;
      }

      // 2. Aguardar Zendesk estar pronto
      await this.waitForZendeskReady();

      // 3. Obter visitorId estável do dispositivo
      const visitorId = await this.getZendeskVisitorId();
      this.log(`VisitorId obtido: ${visitorId}`);

      // 4. Busca inicial do JWT. Serve para dois fins: (a) validar de imediato
      // que o backend responde — falhas aqui entram no circuito de backoff; e
      // (b) popular o cache, que o callback do loginUser reaproveita logo abaixo.
      const jwt = await this.requestMessengerToken(user, visitorId)
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      // Se o componente foi destruído durante a espera, o stream completa sem
      // emitir e `jwt` vem indefinido — não há o que inicializar.
      if (!jwt) {
        return;
      }

      // 5. Registrar autenticação (com renovação) e só então inicializar o
      // widget - `authenticateAndInit` só resolve depois que o Zendesk
      // confirma o `loginUser` (ver seu próprio comentário). Sem esse await,
      // o backoff seria zerado antes de sabermos se a autenticação de fato
      // funcionou.
      this.log('JWT obtido com sucesso');
      await this.authenticateAndInit(user, visitorId);
      this.resetInitBackoff(); // sucesso confirmado: limpa o histórico de falhas
    } catch (error) {
      // NÃO zera `currentUserId`: mantê-lo preserva o "mesmo usuário" e faz o
      // polling respeitar o backoff, em vez de re-tentar imediatamente a cada 5s.
      this.registerInitFailure(error);
    } finally {
      this.initInProgress = false;
    }
  }

  /**
   * Registra uma falha de inicialização, aplicando backoff progressivo e
   * desistindo após `maxInitFailures` tentativas (log único de falha definitiva).
   */
  private registerInitFailure(error: unknown): void {
    this.initFailures++;

    if (this.initFailures >= this.maxInitFailures) {
      this.initAborted = true;
      this.log(
        `Falha ao inicializar após ${this.initFailures} tentativas. Desistindo até o próximo login. Erro: ${error}`,
        'error'
      );
      return;
    }

    const delay = this.retryDelaysMs[Math.min(this.initFailures - 1, this.retryDelaysMs.length - 1)];
    this.nextInitAttemptAt = Date.now() + delay;
    this.log(
      `Falha ao inicializar (tentativa ${this.initFailures}/${this.maxInitFailures}). Retry em ${delay / 1000}s. Erro: ${error}`,
      'warn'
    );
  }

  /**
   * Zera o circuito de retry (novo usuário, logout ou init bem-sucedida).
   */
  private resetInitBackoff(): void {
    this.initFailures = 0;
    this.nextInitAttemptAt = 0;
    this.initAborted = false;
  }

  /**
   * Solicita um JWT do Messenger ao backend para o usuário atual.
   * O ZendeskService cacheia em memória (TTL < validade do token), então
   * chamadas seguidas reaproveitam; na expiração o cache já venceu e vem um novo.
   */
  private requestMessengerToken(user: User, visitorId: string): Observable<string> {
    const authToken = this.localStorageService.get('ls.token');
    return this.zendeskService.obterTokenMessenger(authToken, user.id.toString(), visitorId);
  }

  /**
   * Carrega o snippet do Zendesk no modo Messenger.
   * O loader garante que não haja outro widget do Zendesk na página (ver
   * zendesk-snippet.ts); a rejeição entra no circuito de backoff.
   */
  private loadZendeskScript(): Promise<void> {
    return loadZendeskSnippet(this.modeService.messengerKey);
  }

  /**
   * Aguarda o Zendesk estar pronto
   */
  private waitForZendeskReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 100; // 10 segundos (100 * 100ms)

      const checkZendesk = setInterval(() => {
        attempts++;

        if (window.zE) {
          clearInterval(checkZendesk);
          this.log('Zendesk pronto');
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkZendesk);
          reject(new Error('Timeout ao aguardar Zendesk'));
        }
      }, 100);
    });
  }

  /**
   * Obtém ou gera um visitorId estável
   *
   * Em vez de usar o Zendesk Visitor ID (que requer configuração específica),
   * usamos um UUID gerado localmente e persistido no localStorage.
   * Isso garante que o mesmo navegador/dispositivo sempre tenha o mesmo visitorId.
   */
  private getZendeskVisitorId(): Promise<string> {
    return new Promise((resolve) => {
      const STORAGE_KEY = 'zendesk_visitor_id';

      // Tentar obter do localStorage
      let visitorId = this.localStorageService.get(STORAGE_KEY);

      if (!visitorId) {
        // Gerar novo UUID e salvar
        visitorId = generateUuid();
        this.localStorageService.set(STORAGE_KEY, visitorId);
        this.log(`Novo visitorId gerado: ${visitorId}`);
      } else {
        this.log(`VisitorId existente: ${visitorId}`);
      }

      resolve(visitorId);
    });
  }

  /**
   * Autentica e inicializa o widget Messenger.
   *
   * A autenticação usa RENOVAÇÃO automática: o SDK do Messenger reinvoca o
   * callback do `loginUser` no login inicial E toda vez que o token expira
   * (o backend emite JWT de vida curta — ~2h). Por isso buscamos um JWT novo a
   * CADA chamada em vez de fixar um token; sem isso, a sessão autenticada cairia
   * ao expirar. Na primeira chamada o token vem do cache populado por
   * initMessenger; nas renovações, o cache já venceu e um novo é obtido.
   *
   * A Promise só resolve depois que o Zendesk CONFIRMA o `loginUser` (terceiro
   * argumento, documentado em developer.zendesk.com/api-reference/widget-messaging/web/authentication).
   * Antes disso o widget fica sem tags e sem `show` — é a corrida entre a
   * liberação do widget e a autenticação (PF-4572) que gerava end users
   * anônimos no Zendesk quando a primeira mensagem chegava antes do JWT ser
   * validado. `markReady`/`markNotReady` espelham esse estado para o
   * `MessengerChatAdapter`, que é quem realmente abre o painel a partir do
   * clique no FAB — sem essa segunda guarda, o clique abriria a conversa
   * mesmo com este método ainda pendente.
   */
  private authenticateAndInit(user: User, visitorId: string): Promise<void> {
    if (!window.zE) {
      // Lança para que a falha entre no circuito de backoff em initMessenger,
      // em vez de silenciar e deixar o polling re-tentar de imediato.
      throw new Error('zE não disponível');
    }

    this.log('Registrando autenticação do Messenger (com renovação)...');
    const authStartedAt = Date.now();

    return new Promise<void>((resolve, reject) => {
      // Só a PRIMEIRA autenticação resolve/rejeita esta Promise (renovações
      // reinvocam os callbacks quando o widget já está no ar). `settled`
      // garante isso e cancela o timeout, evitando timer órfão.
      let settled = false;
      let timeoutId: any;
      let destroySubscription: Subscription;

      const settle = (action: () => void): void => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timeoutId);
        if (destroySubscription) {
          destroySubscription.unsubscribe();
        }
        action();
      };

      timeoutId = setTimeout(
        () => settle(() => reject(new Error(`Timeout de ${this.authTimeoutMs}ms aguardando a confirmação do loginUser`))),
        this.authTimeoutMs
      );

      // Destruição do componente durante a espera: o `takeUntil(destroy$)` do
      // stream do JWT completa SEM emitir, então nem `next` nem `error` rodam e
      // a Promise ficaria pendente até o timeout — travando `initInProgress`
      // por até 20s. Liquidar aqui libera o fluxo de imediato.
      destroySubscription = this.destroy$
        .pipe(take(1))
        .subscribe(() => settle(() => reject(new Error('Componente destruído antes da confirmação do loginUser'))));

      window.zE(
        'messenger',
        'loginUser',
        // 1. Autenticar com JWT. O SDK nos passa um `sendToken`; devolvemos
        // por ele um token FRESCO a cada invocação (login inicial e
        // renovações na expiração).
        (sendToken: (token: string) => void) => {
          this.requestMessengerToken(user, visitorId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (jwt) => sendToken(jwt),
              error: (error) => {
                this.log(`Falha ao obter/renovar o JWT do Messenger: ${error}`, 'error');
                // Sem token o SDK não confirma o login: falha rápido em vez de
                // esperar o timeout. Numa renovação o widget já está no ar e o
                // próprio SDK re-tenta, então não há Promise para rejeitar.
                settle(() => reject(error instanceof Error ? error : new Error(String(error))));
              },
            });
        },
        // 2. Confirmação do resultado. Chamado no login inicial e em toda
        // renovação — `widgetInitialized` distingue as duas situações.
        (error: unknown) => {
          if (this.widgetInitialized) {
            if (error) {
              this.log(`Falha ao renovar autenticação do Messenger: ${error}`, 'error');
            }
            return;
          }

          // Troca de conta (sem logout) enquanto o loginUser estava pendente: o
          // resultado é do usuário anterior. Desfaz um login confirmado em vez de
          // liberar o widget com a identidade errada, e não conta uma falha dele
          // no backoff do usuário atual. O polling decide o que fazer com o
          // usuário atual (inicializa de novo, ou nada se a categoria não tem chat).
          if (!this.isStillEligible(user)) {
            this.log(`Usuário ${user.id} não é mais o atual — descartando o login do Messenger`, 'warn');
            if (!error) {
              try {
                window.zE('messenger', 'logoutUser');
              } catch (logoutError) {
                this.log(`Erro ao desfazer o login do Messenger: ${logoutError}`, 'error');
              }
            }
            this.zendeskService.limparCacheMessenger();
            settle(resolve);
            return;
          }

          if (error) {
            this.log(`Falha ao autenticar o Messenger: ${error}`, 'error');
            settle(() => reject(error instanceof Error ? error : new Error(String(error))));
            return;
          }

          this.log(`Messenger autenticado em ${Date.now() - authStartedAt}ms`);

          // 3. Configurar conversation fields (custom fields)
          // ❌ TEMPORARIAMENTE DESABILITADO - Criar fields no Zendesk Admin primeiro
          // Ver: docs/GUIA_CONFIGURACAO_ZENDESK_ADMIN.md
          /*
          window.zE('messenger:set', 'conversationFields', [
            { id: 'physical_user_id', value: user.id.toString() },
            { id: 'page_context', value: window.location.pathname },
          ]);
          */

          // 4. Configurar tags
          const tags = [
            'webapp',
            'messenger',
            `user_${user.id}`,
          ];

          window.zE('messenger:set', 'conversationTags', tags);

          // 5. Só agora deixa o widget disponível (show). NÃO usamos `hide`
          // aqui: no Messaging, `hide` esconde o widget inteiro e só `show`
          // reverte — `open` sozinho não revela um widget escondido, o que
          // impedia o painel de renderizar ao clicar no FAB. O launcher
          // nativo continua oculto por CSS (iframe#launcher), então o
          // controle segue sendo pelo nosso FAB (ChatService → open/close).
          window.zE('messenger', 'show');

          this.widgetInitialized = true;
          this.messengerChatAdapter.markReady();
          this.log('Messenger inicializado com sucesso');
          settle(resolve);
        }
      );
    });
  }

  /**
   * Esconde e faz logout do Messenger
   */
  private hideMessenger(): void {
    // Os dois estados de "autenticado" (`widgetInitialized` aqui e `ready` no
    // adapter) precisam cair JUNTOS e sem depender do `zE`. Se divergirem —
    // `widgetInitialized` true com o adapter bloqueado — a branch de renovação
    // em authenticateAndInit retorna cedo, nunca chama `markReady()` de novo e
    // o FAB fica inerte mesmo após uma reautenticação bem-sucedida.
    // O MessengerChatAdapter é singleton (`providedIn: 'root'`) e sobrevive à
    // destruição deste componente, então herdaria `ready = true` da sessão
    // anterior sem este reset.
    const estavaInicializado = this.widgetInitialized;
    this.widgetInitialized = false;
    this.messengerChatAdapter.markNotReady();

    if (window.zE && estavaInicializado) {
      try {
        window.zE('messenger', 'hide');
        window.zE('messenger', 'logoutUser');
        this.zendeskService.limparCacheMessenger();
        this.log('Messenger desconectado');
      } catch (error) {
        this.log(`Erro ao desconectar Messenger: ${error}`, 'error');
      }
    }
  }

  /**
   * Remove o script do Zendesk injetado no `<head>`.
   * Fecha o ciclo de vida em SPA: sem isso, o `<script>` sobreviveria à
   * destruição do componente e deixaria estado órfão.
   */
  private removeZendeskScript(): void {
    removeZendeskSnippet();
    this.scriptLoaded = false;
  }

  /**
   * Log helper — delega ao logger compartilhado do Zendesk para manter o
   * mesmo prefixo/formato dos demais componentes e facilitar o filtro no console.
   */
  private log(message: string, level: ZendeskLogLevel = 'log'): void {
    zendeskLog('ZendeskMessenger', message, level);
  }
}

